import React, { useState, useEffect } from 'react';
import { Product } from '../constants';
import { useLanguage } from '../contexts/LanguageContext';
import { Package, Plus, Trash2, X, DollarSign, Scale, ExternalLink, Edit2, AlertCircle } from 'lucide-react';

interface EditableProduct extends Product {
  isEditing?: boolean;
}

export const ProductsPage: React.FC = () => {
  const { t, dir } = useLanguage();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);

  const [newProduct, setNewProduct] = useState({
    name: '',
    purchase_value_usd: '',
    estimated_weight_kg: '',
    shipping_cost_per_kg_usd: '',
    purchase_link: ''
  });

  const [editingProduct, setEditingProduct] = useState<EditableProduct | null>(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/products');
      const data = await response.json();
      if (data.success) {
        setProducts(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newProduct.name,
          purchase_value_usd: parseFloat(newProduct.purchase_value_usd),
          estimated_weight_kg: parseFloat(newProduct.estimated_weight_kg),
          shipping_cost_per_kg_usd: parseFloat(newProduct.shipping_cost_per_kg_usd),
          purchase_link: newProduct.purchase_link
        })
      });
      const data = await response.json();
      if (data.success) {
        setProducts(prev => [...prev, data.data]);
        setNewProduct({
          name: '',
          purchase_value_usd: '',
          estimated_weight_kg: '',
          shipping_cost_per_kg_usd: '',
          purchase_link: ''
        });
        setShowAddForm(false);
        setError(null);
      }
    } catch (err) {
      setError(t.failedToAddProduct);
    }
  };

  const handleDeleteProduct = async (id: number) => {
    try {
      const response = await fetch(`/api/products?id=${id}`, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        setProducts(prev => prev.filter(p => p.id !== id));
        setShowDeleteConfirm(null);
        setError(null);
      }
    } catch (err) {
      setError(t.failedToDeleteProduct);
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct) return;
    try {
      const response = await fetch(`/api/products?id=${editingProduct.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editingProduct.name,
          purchase_value_usd: editingProduct.purchase_value_usd,
          estimated_weight_kg: editingProduct.estimated_weight_kg,
          shipping_cost_per_kg_usd: editingProduct.shipping_cost_per_kg_usd,
          purchase_link: editingProduct.purchase_link
        })
      });
      const data = await response.json();
      if (data.success) {
        setProducts(prev => prev.map(p => p.id === editingProduct.id ? data.data : p));
        setEditingProduct(null);
        setError(null);
      }
    } catch (err) {
      setError(t.failedToUpdateProduct);
    }
  };

  const startEditing = (product: Product) => {
    setEditingProduct({ ...product, isEditing: true });
  };

  return (
    <div className="bg-neo-white border-4 border-neo-black shadow-neo overflow-hidden">
      <div className="p-6 md:p-8">
        <div className="mb-6 pb-6 border-b-4 border-neo-black flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="text-2xl font-bold text-neo-black font-mono uppercase">{t.productsPageTitle}</h3>
            <p className="text-neo-black mt-1 font-mono text-sm">{t.productsPageDesc}</p>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-neo-green border-4 border-neo-black px-4 py-2 text-sm font-bold font-mono shadow-neo-sm flex items-center gap-2 hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
          >
            <Plus className="w-4 h-4" />
            {t.addProduct}
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-neo-red border-4 border-neo-black text-neo-black px-4 py-3 shadow-neo-sm flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span className="font-mono font-bold">{error}</span>
          </div>
        )}

        {showAddForm && (
          <form onSubmit={handleAddProduct} className="bg-neo-yellow border-4 border-neo-black p-6 shadow-neo mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold font-mono uppercase">{t.newProduct}</h4>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="bg-neo-red border-4 border-neo-black p-1 shadow-neo-sm"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder={t.productName}
                value={newProduct.name}
                onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                required
              />
              <input
                type="number"
                step="0.01"
                placeholder={t.valueUsd}
                value={newProduct.purchase_value_usd}
                onChange={(e) => setNewProduct({...newProduct, purchase_value_usd: e.target.value})}
                className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                required
              />
              <input
                type="number"
                step="0.01"
                placeholder={t.weightKg}
                value={newProduct.estimated_weight_kg}
                onChange={(e) => setNewProduct({...newProduct, estimated_weight_kg: e.target.value})}
                className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                required
              />
              <input
                type="number"
                step="0.01"
                placeholder={t.shippingCostPerKg}
                value={newProduct.shipping_cost_per_kg_usd}
                onChange={(e) => setNewProduct({...newProduct, shipping_cost_per_kg_usd: e.target.value})}
                className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                required
              />
              <input
                type="url"
                placeholder={t.purchaseLink}
                value={newProduct.purchase_link}
                onChange={(e) => setNewProduct({...newProduct, purchase_link: e.target.value})}
                className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm md:col-span-2"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full mt-4 bg-neo-blue border-4 border-neo-black py-2 text-sm font-bold font-mono shadow-neo-sm hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
            >
              {t.createProduct}
            </button>
          </form>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-neo-black font-mono">Loading...</div>
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-neo-black">
            <Package className="w-16 h-16 mb-4" />
            <p className="text-lg font-bold font-mono">{t.noProducts}</p>
            <p className="text-sm mt-2 font-mono">{t.noProductsDesc}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {products.map((product) => (
              <div key={product.id} className="bg-neo-white border-4 border-neo-black p-4 shadow-neo-sm">
                {editingProduct?.id === product.id ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-neo-black font-mono">{t.editProduct}</span>
                      <button
                        onClick={() => setEditingProduct(null)}
                        className="bg-neo-red border-4 border-neo-black p-1 shadow-neo-sm"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={editingProduct.name}
                        onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
                        className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={editingProduct.purchase_value_usd}
                        onChange={(e) => setEditingProduct({...editingProduct, purchase_value_usd: parseFloat(e.target.value) || 0})}
                        className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={editingProduct.estimated_weight_kg}
                        onChange={(e) => setEditingProduct({...editingProduct, estimated_weight_kg: parseFloat(e.target.value) || 0})}
                        className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={editingProduct.shipping_cost_per_kg_usd}
                        onChange={(e) => setEditingProduct({...editingProduct, shipping_cost_per_kg_usd: parseFloat(e.target.value) || 0})}
                        className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm"
                      />
                      <input
                        type="url"
                        value={editingProduct.purchase_link}
                        onChange={(e) => setEditingProduct({...editingProduct, purchase_link: e.target.value})}
                        className="w-full px-3 py-2 border-4 border-neo-black text-sm bg-neo-white font-mono focus:outline-none shadow-neo-sm md:col-span-2"
                      />
                    </div>
                    <button
                      onClick={handleUpdateProduct}
                      className="w-full bg-neo-green border-4 border-neo-black py-2 text-sm font-bold font-mono shadow-neo-sm hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
                    >
                      {t.save}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-neo-black font-mono mb-2">{product.name}</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-neo-black" />
                            <span className="font-mono">{t.valueUsd}:</span>
                            <span className="font-bold font-mono">${product.purchase_value_usd.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Scale className="w-4 h-4 text-neo-black" />
                            <span className="font-mono">{t.weightKg}:</span>
                            <span className="font-bold font-mono">{product.estimated_weight_kg.toFixed(2)} kg</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-neo-black" />
                            <span className="font-mono">{t.shippingCostPerKg}:</span>
                            <span className="font-bold font-mono">${product.shipping_cost_per_kg_usd.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono bg-neo-yellow border-4 border-neo-black px-2 py-0.5 shadow-neo-sm">
                              {t.estTotal} ${((product.purchase_value_usd + (product.estimated_weight_kg * product.shipping_cost_per_kg_usd))).toFixed(2)}
                            </span>
                          </div>
                        </div>
                        <a
                          href={product.purchase_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-neo-black font-mono font-bold hover:underline mt-2"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {t.link}
                        </a>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => startEditing(product)}
                          className="bg-neo-blue border-4 border-neo-black p-2 shadow-neo-sm hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
                          title={t.editProduct}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(product.id)}
                          className="bg-neo-red border-4 border-neo-black p-2 shadow-neo-sm hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
                          title={t.deleteProduct}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {showDeleteConfirm === product.id && (
                  <div className="mt-4 bg-neo-red border-4 border-neo-black p-4 shadow-neo-sm">
                    <p className="font-mono text-sm mb-3">{t.confirmDelete}</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDeleteProduct(product.id)}
                        className="flex-1 bg-neo-black border-4 border-neo-black py-2 text-sm font-bold font-mono text-neo-white shadow-neo-sm hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
                      >
                        {t.deleteProduct}
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(null)}
                        className="flex-1 bg-neo-white border-4 border-neo-black py-2 text-sm font-bold font-mono shadow-neo-sm hover:translate-x-1 hover:-translate-y-1 hover:shadow-neo transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
